/**
 * @copyright (C) 2024, rsupport. All rights reserved
 *
 * @license
 * This software and/or source code may be used, copied and/or disseminated only
 * with the written permission of rsupport, or in accordance with the terms
 * and conditions stipulated in the agreement/contract under which the software
 * and/or source code has been supplied by rsupport or its affiliates.
 * Unauthorized use, copying, or dissemination of this file, via any medium, is
 * strictly prohibited, and will constitute an infringement of copyright.
 *
 * @author
 * SoonKi Min (skmin@rsupport.com)
 *
 * @description
 *
 * @created at Wed Jan 17 2024
 **/

import { useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";

import { Divider, Icon, ModalPopup, Typography, useModal, useToast } from "@/frameworks/designStuff/components";
import { _T } from "@rfice/language";
import { ModalID } from "@/frameworks/core/commons";
import { useFileManager } from "@/frameworks/core/hooks";

import * as S from "./emotion";
import { SubFolder } from "../../../../../components/Folders";

type SelectedFolderType = {
	id: string;
	name: string;
};

export default function FileMoveModal({
	file,
	onClose
}: {
	file: Required<FileBox.Folders.GetFolderFileListResponse>["content"][number];
	onClose?: () => void;
}) {
	const { hide } = useModal();
	const { myRoomID, currentFolder, moveFolder, moveFile } = useFileManager();
	const queryClient = useQueryClient();
	const toast = useToast();

	const myFolders = queryClient.getQueryData<FileBox.Folders.GetMyFoldersResponse>(["myFolders", myRoomID]);
	const [selectedFolder, setSelectedFolder] = useState<SelectedFolderType>({ id: myRoomID, name: _T("내 파일") });
	const [isOpenSubfolders, setIsOpenSubfolders] = useState(false);

	const { mutate } = useMutation({
		mutationKey: ["moveFile", file.id, selectedFolder.id],
		mutationFn: () => {
			if (file.fileBoxType === "FILE") {
				return moveFile({ id: file.id, folderId: selectedFolder.id });
			}
			return moveFolder({
				id: file.id,
				parentFolderId: selectedFolder.id,
				referenceId: myRoomID,
				referenceType: "INDIVIDUAL"
			});
		},
		onSuccess() {
			toast.show({
				type: "system",
				message: _T("{{folder}} 폴더로 {{file}}이 이동되었습니다.", {
					folder: selectedFolder.name,
					file: file.name
				})
			});
			queryClient.refetchQueries({
				queryKey: ["folderFileList", { all: currentFolder.all, id: currentFolder.id }]
			});
			queryClient.refetchQueries({ queryKey: ["folderFileList", { id: selectedFolder.id }] });
			handleClose();
		},
		onError() {
			toast.show({
				type: "error",
				message: _T("해당 경로로 폴더를 이동할 수 없습니다.")
			});
		}
	});

	const handleClose = () => {
		hide(ModalID.MOVE_FILE);
		onClose?.();
	};

	return (
		<ModalPopup
			modalID={ModalID.MOVE_FILE}
			title={_T("파일 이동")}
			titleTypography={{ typoSize: "Title1" }}
			cancelText={_T("취소")}
			confirmText={_T("이동")}
			onConfirm={() => mutate()}
		>
			<Divider type="line" />
			<Typography typoColor="neutral_60_100">{_T("이동할 위치 선택")}</Typography>
			<S.FileBoxWrapper
				gap={4}
				focus={selectedFolder.id === myRoomID}
				verticalAlign="center"
				css={{ padding: "4px 6px", margin: "4px 0" }}
				onClick={() => {
					setIsOpenSubfolders(!isOpenSubfolders);
					setSelectedFolder({
						id: myRoomID || "",
						name: _T("내 파일")
					});
				}}
			>
				<S.MyFileArrowIcon open={isOpenSubfolders} name="DownFilledBig16" size={16} />
				<Icon name="Filebox24" size={24} />
				<Typography typoSize="Title3" typoColor="neutral_80_100">
					{_T("내 파일")}
				</Typography>
			</S.FileBoxWrapper>
			{isOpenSubfolders &&
				myFolders?.map((folder: FileBox.Folders.GetMyFoldersResponse[number]) => {
					return (
						<SubFolder
							key={folder.id}
							folder={{
								...folder,
								isMyFile: true,
								referenceType: "INDIVIDUAL",
								referenceID: myRoomID,
								folderType: "INDIVIDUAL",
								parent: {
									id: myRoomID,
									name: _T("내 파일"),
									existsSubFolder: true,
									isMyFile: true,
									referenceType: "INDIVIDUAL",
									referenceID: myRoomID,
									folderType: "INDIVIDUAL"
								}
							}}
							focusedBy={selectedFolder.id}
							onSelectFolder={(targetFolder) => {
								setSelectedFolder({ id: targetFolder.id, name: targetFolder.name ?? "" });
							}}
						/>
					);
				})}
		</ModalPopup>
	);
}
